import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
} from '@mui/material'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { ALLOWED_POS_IDS, PosId } from '../../config/posIds'
import { usePosId } from '../../hooks/usePosId'
import { usePrinter } from '../../hooks/usePrinter'
import { usePrintingEnabled } from '../../hooks/usePrintingEnabled'
import { checkPrinterAlive } from '../../service/print.service'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { userOptionsActions } from '../../store/slices/userOptionsSlice'

export const Settings: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isDarkTheme = useAppSelector((state) => state.userOptions.darkTheme)
  const { posId, setPosId } = usePosId()
  const { printerIp, printerName, setPrinterIp, setPrinterName } = usePrinter()
  const { printingEnabled, setPrintingEnabled } = usePrintingEnabled()
  const [testing, setTesting] = useState(false)

  const handleTestConnection = async () => {
    if (!printerIp) return
    setTesting(true)
    try {
      const alive = await checkPrinterAlive(printerIp)
      if (alive) {
        toast.success(t('printerSettings.testSuccess'))
      } else {
        toast.error(t('printerSettings.testFailed'))
      }
    } catch {
      toast.error(t('printerSettings.testFailed'))
    } finally {
      setTesting(false)
    }
  }

  return (
    <ScreenWrapper title={t('settings.title')}>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
        <FormControl size="small">
          <InputLabel>{t('settings.posId')}</InputLabel>
          <Select
            value={posId}
            label={t('settings.posId')}
            onChange={(e: SelectChangeEvent) => setPosId(e.target.value as PosId)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {t('settings.posIdPlaceholder')}
            </MenuItem>
            {ALLOWED_POS_IDS.map((id) => (
              <MenuItem key={id} value={id}>
                {id}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{t('settings.posIdHelper')}</FormHelperText>
        </FormControl>
        <TextField
          label={t('printerSettings.form.name')}
          value={printerName}
          onChange={(e) => setPrinterName(e.target.value)}
          size="small"
          disabled={!printingEnabled}
        />
        <TextField
          label={t('printerSettings.form.ip')}
          value={printerIp}
          onChange={(e) => setPrinterIp(e.target.value)}
          size="small"
          placeholder="192.168.1.100"
          disabled={!printingEnabled}
        />
        <Button
          variant="outlined"
          onClick={handleTestConnection}
          disabled={!printerIp || testing || !printingEnabled}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('printerSettings.testConnection')}
        </Button>
        <FormControl>
          <FormControlLabel
            control={<Switch checked={printingEnabled} onChange={(e) => setPrintingEnabled(e.target.checked)} />}
            label={t('settings.printReceipts')}
          />
          <FormHelperText>{t('settings.printReceiptsHelper')}</FormHelperText>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={isDarkTheme} onChange={() => dispatch(userOptionsActions.toggleTheme())} />}
          label={t('settings.darkMode')}
        />
      </Box>
    </ScreenWrapper>
  )
}
